import type { SchematizedValues } from "components/Form/components/SchemaForm/SchemaForm.types";
import { IconCollection } from "components/Icon/IconCollection";
import { ResourceConfig } from "config/config";
import {
    DeleteListAction,
    MigrateDeprecationsListAction,
    PermanentlyDeleteListAction,
    deleteActionSignature,
    deletePermanentlyActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    migrateDeprecationsActionSignature,
    moveToLocationActionSignature,
} from "containers/Resources/components/CommonActions/CommonActions";
import {
    commentsTab,
    connectorEgressPoliciesTab,
    logsTab,
    metadataTab,
    referencesUsedByTab,
    valueAndSchema,
    valueAndSchemaTab,
    valueAndSchemaTabKey,
    wrappersTab,
} from "containers/Resources/screens/ResourcesSingle/constants";
import { buildSimpleColumn } from "containers/Search/components/SearchTabs/columns";
import { YAMLException } from "js-yaml";
import { isEqual } from "lodash";
import { parseYaml } from "utils/valueTransformers";
import "./connector.scss";
import resource from "./resource";
import { getLatestExecutions } from "./sharedFields";

const resourceName = "connector";

const config: ResourceConfig = {
    label: "Connector",
    icon: IconCollection.connection,
    resourceName,
    resourceClass: "resource",
    defaultActiveTab: valueAndSchemaTabKey,
    layout: [
        { partial: "pageHeaderForm" },
        { partial: "form" },
        {
            partial: "tabs",
            config: {
                tabs: [
                    valueAndSchemaTab.tab,
                    wrappersTab.tab,
                    logsTab.tab,
                    commentsTab.tab,
                    referencesUsedByTab.tab,
                    connectorEgressPoliciesTab.tab,
                    metadataTab.tab,
                ],
                content: [
                    valueAndSchemaTab.content,
                    wrappersTab.content,
                    logsTab.content,
                    commentsTab.content,
                    referencesUsedByTab.content,
                    connectorEgressPoliciesTab.content,
                    metadataTab.content,
                ],
            },
        },
    ],
    actions: {
        shown: [migrateDeprecationsActionSignature],
        hidden: [
            duplicateActionSignature,
            exportActionSignature,
            moveToLocationActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
            { key: "migrateDeprecations", action: MigrateDeprecationsListAction },
        ],
    },
    additionalColumns: (setNewValue) => [
        {
            label: "Connector Type",
            key: "connector_type",
            shown: true,
            sortable: true,
            dto: (value: any) => {
                const content = <div>{value.connector_type}</div>;
                return buildSimpleColumn(
                    content,
                    setNewValue
                        ? () =>
                              setNewValue({ field: "connector_type", value: value.connector_type })
                        : undefined
                );
            },
        },
    ],
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            connector_type
            value
            input_schema
            priority
            max_runtime_minutes
            has_deprecation
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            connector_type
            has_deprecation
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)}
            $connector_type: ConnectorType!
            $value: SchemalessData,
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            connector_type: $connector_type,
            value: $value,
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $value: SchemalessData,
            $connector_type: ConnectorType
            $priority: Int
            $max_runtime_minutes: Int
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            value: $value,
            connector_type: $connector_type,
            priority: $priority
            max_runtime_minutes: $max_runtime_minutes
        `,
    },
    statsSidebar: [getLatestExecutions("connector")],
    form: [
        {
            name: valueAndSchema,
            component: "schemaForm", // TODO: use form with input_schema https://app.clickup.com/t/860q6x5p1
            fieldProps: {
                lang: "yaml",
                isReadOnly: false,
                canEnableSaveManually: true,
            },
            remappingSubmitDto: (value: SchematizedValues) => {
                return [
                    {
                        key: "value",
                        value: value.values,
                    },
                    {
                        key: "input_schema",
                        value: value.schema,
                    },
                ];
            },
            isEqual: (valueA, valueB) => {
                return isEqual(valueA, valueB);
            },
            validation: (value) => {
                try {
                    parseYaml(value.values);
                    return undefined;
                } catch (error: any) {
                    const typedError: YAMLException = error;
                    const errorMessage = `${typedError?.name}: ${typedError?.reason}`;
                    return errorMessage;
                }
            },
        },
    ],
    formDTO: {
        initialValueDTO: (resourceData: any) => {
            const currentSchema = resourceData?.input_schema;
            const valueAndSchemaFieldValues: SchematizedValues = {
                values: resourceData?.value,
                schema: currentSchema,
            };

            return { ...resourceData, [valueAndSchema]: valueAndSchemaFieldValues };
        },
    },
};
export default config;
